import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, Flame, RefreshCw } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { SectionCard, Stamp } from "./kit";
import { useAuth } from "../context/AuthContext";
import { isAdminRole } from "../lib/constants";
import { toast } from "sonner";

export default function ScoreDigest() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("rising");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/scoring/digest/latest").then(({ data }) => setD(data)).catch(() => setD({})), []);
  useEffect(() => { load(); }, [load]);
  const generate = async () => { setBusy(true); try { await api.post("/scoring/digest"); toast.success("Digest refreshed"); load(); } catch (e) { toast.error(apiErr(e)); } setBusy(false); };

  const rows = d?.[tab] || [];
  const TABS = [["rising", "Rising", TrendingUp, "text-emerald-600"], ["falling", "Falling", TrendingDown, "text-red-600"], ["hot", "Hot now", Flame, "text-orange-500"]];
  return (
    <SectionCard title="Weekly Score Digest" testid="score-digest" action={
      <div className="flex items-center gap-2">
        {d?.week && <span className="text-[11px] text-slate-400">{d.week} · {d.total_leads} leads · avg {d.avg_score}</span>}
        {isAdminRole(user?.role) && <button onClick={generate} disabled={busy} className="text-xs text-slate-500 hover:text-navy-800 flex items-center gap-1" data-testid="digest-refresh"><RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />Refresh</button>}
      </div>}>
      {!d?.week ? (
        <div className="text-sm text-slate-500 py-4 text-center" data-testid="digest-empty">
          The first digest is generated automatically on Monday morning.
          {isAdminRole(user?.role) && <button onClick={generate} className="block mx-auto mt-2 text-navy-800 font-semibold hover:text-gold-dark">Generate now →</button>}
        </div>
      ) : (
        <>
          <div className="flex gap-1.5 mb-3">
            {TABS.map(([k, l, I, c]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`digest-tab-${k}`} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border transition-[background-color,border-color] ${tab === k ? "bg-navy-800 text-white border-navy-800" : "bg-white text-slate-600 border-slate-200 hover:border-gold"}`}>
                <I className={`w-3 h-3 ${tab === k ? "" : c}`} />{l} <span className="opacity-60">{(d[k] || []).length}</span>
              </button>
            ))}
          </div>
          {rows.length === 0 ? <p className="text-sm text-slate-400 py-3 text-center">Nothing in this list for the week.</p> : (
            <div className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <button key={r.contact_id} onClick={() => nav(`/contacts/${r.contact_id}`)} data-testid={`digest-row-${r.contact_id}`} className="w-full flex items-center gap-3 py-2 text-left hover:bg-slate-50 rounded-md px-1">
                  <span className="w-5 text-xs text-slate-400 text-right">{i + 1}</span>
                  <span className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy-800 truncate">{r.name}</p>
                    <p className="text-[11px] text-slate-400">{[r.contact_type, r.temperature, r.assigned_to_name].filter(Boolean).join(" · ")}</p>
                  </span>
                  <span className="font-display font-bold text-navy-800">{r.score}</span>
                  {tab !== "hot" && <span className={`text-xs font-semibold w-12 text-right ${r.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>{r.delta > 0 ? "+" : ""}{r.delta}</span>}
                </button>
              ))}
            </div>
          )}
          <div className="mt-3 pt-2 border-t border-slate-100"><Stamp prefix="Generated" at={d.generated_at} /></div>
        </>
      )}
    </SectionCard>
  );
}
